import { useEffect, useState, type KeyboardEvent } from 'react'
import { Globe, Lock, X } from 'lucide-react'

import { Button } from '../../ui/Button'
import { canonicalAllowFromEntry, MAX_ALLOW_FROM_ENTRIES } from '../../core/allowFrom'
import type { GuidedPort } from '../../core/guided'

/**
 * The caller's address for "Add my IP", owned by the route so it is fetched
 * only once a port is restricted, and never from a third-party service.
 */
export interface MyIpState {
  status: 'idle' | 'loading' | 'ready' | 'error'
  ip?: string | undefined
  /** Ask for it; idempotent. */
  request: () => void
}

export const NO_MY_IP: MyIpState = { status: 'idle', request: () => undefined }

/** "Only 2 addresses" — the restricted badge in a port row. */
export function restrictionLabel(count: number): string {
  return `Only ${count} address${count === 1 ? '' : 'es'}`
}

/** The canonical entry for a bare address, as "Add my IP" adds it. */
function myEntry(ip: string | undefined): string | null {
  if (!ip) return null
  const c = canonicalAllowFromEntry(ip)
  return c.ok ? c.value : null
}

/**
 * "Who can connect" for one port: Anyone, or only the listed addresses.
 * Entries are stored canonical (core/allowFrom.ts), so what the tenant sees in
 * a chip is exactly what the signed manifest will carry.
 */
export function PortAccess({ port, index, error, submitted, myIp, onChange }: {
  port: GuidedPort
  index: number
  /** The form's error for this port's access, e.g. an empty list. */
  error?: string | undefined
  submitted: boolean
  myIp: MyIpState
  onChange: (allowFrom: string[] | undefined) => void
}) {
  const [entry, setEntry] = useState('')
  const [entryError, setEntryError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const restricted = port.allowFrom !== undefined
  const list = port.allowFrom ?? []
  const mine = myEntry(myIp.ip)
  const portLabel = port.container ? `port ${port.container}` : 'this port'

  const { request } = myIp
  useEffect(() => { if (restricted) request() }, [restricted, request])

  const add = (raw: string) => {
    const c = canonicalAllowFromEntry(raw)
    if (!c.ok) { setEntryError(c.message); setNote(null); return }
    if (list.includes(c.value)) { setEntryError(`${c.value} is already in the list.`); setNote(null); return }
    if (list.length >= MAX_ALLOW_FROM_ENTRIES) { setEntryError(`A port can have at most ${MAX_ALLOW_FROM_ENTRIES} addresses.`); setNote(null); return }
    onChange([...list, c.value])
    setEntry('')
    setEntryError(null)
    setNote(c.note ?? null)
  }
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    // Enter adds the entry; it must not submit the whole configure form.
    event.preventDefault()
    add(entry)
  }

  const inputId = `port-allow-input-${index}`
  const errorId = `${inputId}-error`
  const shownError = entryError ?? (submitted && error ? error : null)

  return <div className="port-access">
    <div className="port-access-choice">
      <span className="port-access-label" id={`port-access-label-${index}`}>Who can connect</span>
      <fieldset className="segmented port-access-segmented" aria-labelledby={`port-access-label-${index}`}>
        <legend className="sr-only">Who can connect to {portLabel}</legend>
        <span>
          <input className="sr-only" type="radio" id={`port-access-${index}-any`} name={`port-access-${index}`} checked={!restricted} onChange={() => { onChange(undefined); setEntryError(null); setNote(null) }} />
          <label htmlFor={`port-access-${index}-any`}>Anyone</label>
        </span>
        <span>
          <input className="sr-only" type="radio" id={`port-access-${index}-only`} name={`port-access-${index}`} checked={restricted} onChange={() => onChange(list)} />
          <label htmlFor={`port-access-${index}-only`}>Only these IPs</label>
        </span>
      </fieldset>
    </div>

    {restricted && <div className="port-access-editor" role="group" aria-label={`Addresses allowed to connect to ${portLabel}`}>
      {list.length > 0
        ? <ul className="allow-chips" aria-label="Allowed addresses">
          {list.map((value) => <li className="allow-chip" key={value}>
            <code>{value}</code>
            {value === mine && <span className="allow-chip-mine">your IP</span>}
            <button type="button" className="allow-chip-remove" aria-label={`Remove ${value}`} onClick={() => { onChange(list.filter((item) => item !== value)); setNote(null) }}><X aria-hidden="true" size={14} /></button>
          </li>)}
        </ul>
        : <p className="allow-empty">Add at least one address, or choose Anyone.</p>}

      <div className="allow-add">
        <div className="field allow-add-field">
          <label className="field-label" htmlFor={inputId}>Address or range</label>
          <input id={inputId} className="allow-add-input" type="text" value={entry} spellCheck={false} autoComplete="off" autoCapitalize="none" placeholder="203.0.113.7 or a range" aria-invalid={shownError ? true : undefined} aria-describedby={shownError ? errorId : note ? `${inputId}-note` : undefined} onChange={(event) => { setEntry(event.target.value); setEntryError(null) }} onKeyDown={onKeyDown} />
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={() => add(entry)}>Add</Button>
        <Button type="button" size="sm" disabled={myIp.status !== 'ready' || !mine || list.includes(mine)} onClick={() => { if (myIp.ip) add(myIp.ip) }}>
          <Globe aria-hidden="true" size={16} />
          {myIp.status === 'ready' && myIp.ip ? `Add my IP · ${myIp.ip}` : myIp.status === 'error' ? 'Your IP is unavailable' : 'Finding your IP…'}
        </Button>
      </div>
      {shownError && <span className="field-error allow-add-message" id={errorId}>{shownError}</span>}
      {!shownError && note && <span className="field-help allow-add-message" id={`${inputId}-note`}>{note}</span>}

      <p className="field-help">Only these addresses can connect to {portLabel}; everyone else gets no answer. It’s not a password, so keep your app’s login on. The list can’t be changed after launch yet.</p>
    </div>}
  </div>
}

/** The lock badge beside "Assigned at launch" for a restricted port. */
export function RestrictedBadge({ count }: { count: number }) {
  return <span className="port-restricted-badge"><Lock aria-hidden="true" size={13} />{restrictionLabel(count)}</span>
}
